/**
 * MasterDataPage — 1:1 Access port of `frmMdMain` (Caption "Master Data").
 *
 * Sidebar carries the top + inner navigation (Master Data > Futures
 * Control > Calendar | Month | Holiday  AND  Master Data > Item &
 * Valuation > CP List | Items and Valuation | Other List), so the page
 * itself shows only the leaf content + (for Item & Valuation tabs) a
 * "Go to Page" Select for the deepest-level entries — that combo
 * mirrors Access `cbPreferencesList` on each sub-form:
 *   - CP List               → frmMdMainModItemsCPList
 *   - Items and Valuation   → frmMdMainModItemsValuation
 *   - Other List            → frmMdMainModItemsOther
 *
 * URL grammar:
 *   /master-data                                          → redirect default
 *   /master-data/futures-control/{calendar|month|holiday}
 *   /master-data/items/{cp-list|items-valuation|other}/{slug}
 */
import {
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
} from '@mui/material'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import PageHeader from '../components/PageHeader'
import { CARD_CONTENT_COMPACT } from '../theme/responsive'
import ComingSoonPage from './ComingSoonPage'
import { getUnbuild } from '../config/pageBuildStatus'

// Restore reference (parked 2026-05-21):
//   Khi resume các leaf views, import lại và wire vào LEAF_RENDERERS map dưới.
//   Các file view + page refs đã giữ nguyên — không xoá:
//     ./masterData/MasterDataCalendarView
//     ./masterData/MasterDataMonthView
//     ./masterData/MasterDataHolidayView
//     ./refs/{Banks,Certifications,DeliveryPlaces,DeliveryTerms,LicensesAdmin,
//             Packages,Qualities,Updates}Page

// ── Futures Control: inner tabs (frmMdMainModFuture nav buttons).
const FUTURES_INNER_TABS = [
  { slug: 'calendar', label: 'Calendar' },
  { slug: 'month', label: 'Month' },
  { slug: 'holiday', label: 'Holiday' },
] as const

// ── Item & Valuation: inner tabs (frmMdMainModItems nav buttons).
const ITEMS_INNER_TABS = [
  { slug: 'cp-list', label: 'CP List' },
  { slug: 'items-valuation', label: 'Items and Valuation' },
  { slug: 'other', label: 'Other List' },
] as const

const INNER_TABS_SX = {
  borderBottom: 1,
  borderColor: 'divider',
  minHeight: 34,
  '& .MuiTabs-indicator': {
    height: 2.5,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    position: 'relative',
    minHeight: 34,
    minWidth: 0,
    py: 0.25,
    px: 1.5,
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: 0.15,
    textTransform: 'none',
    color: 'text.secondary',
    borderRadius: '6px 6px 0 0',
    transition: 'color 140ms ease, background-color 140ms ease',
    '&:hover': {
      color: 'text.primary',
      bgcolor: 'action.hover',
    },
    '&.Mui-selected': {
      color: 'primary.main',
      fontWeight: 700,
    },
    '&:not(:last-of-type)::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      height: 16,
      width: '1px',
      bgcolor: 'divider',
      opacity: 0.6,
      transition: 'opacity 140ms ease',
    },
    '&:hover::after, &.Mui-selected::after': { opacity: 0 },
  },
} as const

// Restore reference (parked 2026-05-21): các option slug + LEAF_RENDERERS
// map cũ giữ lại để khi resume Master Data, copy lại trực tiếp. Hiện tại 6
// inner tabs đều render ComingSoonPage chuẩn (không show "Go to Page" Select).
//
//   CP_LIST_OPTIONS         (vendor, cp-bank, license, service-type, service, doc-dispatch)
//   ITEMS_VALUATION_OPTIONS (general-info, valuation, updates, type-of-inspection,
//                            dunnage, shipping-doc, claim-condition, type-of-claim,
//                            list-warehouse, processing-type)
//   OTHER_LIST_OPTIONS      (contract-type, certification, bank, delivery-term,
//                            package, delivery-place, tlc-bank, consignee, cvbb-type,
//                            export-terminal, vessel-name, logistic-cost)
//
//   LEAF_RENDERERS:
//     calendar → MasterDataCalendarView
//     month    → MasterDataMonthView
//     holiday  → MasterDataHolidayView
//     license  → LicensesAdminPage
//     valuation→ QualitiesPage
//     updates  → UpdatesPage
//     bank     → BanksPage
//     certification → CertificationsPage
//     delivery-term  → DeliveryTermsPage
//     delivery-place → DeliveryPlacesPage
//     package  → PackagesPage

const renderLeaf = (label: string, path: string) => (
  <ComingSoonPage embedded title={label} status={getUnbuild(path)?.status} />
)

export default function MasterDataPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Parse URL: /master-data / {top} / {inner?} / {leaf?}
  const segments = location.pathname.split('/').filter(Boolean)
  const topSeg = segments[1] as 'futures-control' | 'items' | undefined
  const innerSeg = segments[2] as string | undefined

  // Default-redirect when URL is partial. Mirrors Access Form_Load defaults
  // (frmMdMain → Item & Valuation → CP List; frmMdMainModFuture → Calendar).
  // Leaf-of-leaf (vendor / general-info / ...) parked — không redirect xuống
  // level 4 nữa.
  useEffect(() => {
    if (!topSeg) {
      navigate('/master-data/futures-control/calendar', { replace: true })
      return
    }
    if (topSeg === 'futures-control' && !innerSeg) {
      navigate('/master-data/futures-control/calendar', { replace: true })
      return
    }
    if (topSeg === 'items' && !innerSeg) {
      navigate('/master-data/items/cp-list', { replace: true })
    }
  }, [topSeg, innerSeg, navigate])

  // Branch: Futures Control — inner Tabs [Calendar | Month | Holiday].
  if (topSeg === 'futures-control') {
    const activeInner =
      FUTURES_INNER_TABS.find((t) => t.slug === innerSeg) ?? FUTURES_INNER_TABS[0]
    const innerIndex = FUTURES_INNER_TABS.indexOf(activeInner)
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeader title={`Master Data — Futures Control — ${activeInner.label}`} />
        <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={innerIndex < 0 ? 0 : innerIndex}
            onChange={(_, value: number) => {
              const next = FUTURES_INNER_TABS[value]
              if (next) navigate(`/master-data/futures-control/${next.slug}`)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={INNER_TABS_SX}
          >
            {FUTURES_INNER_TABS.map((tab) => (
              <Tab key={tab.slug} label={tab.label} />
            ))}
          </Tabs>
          <CardContent sx={CARD_CONTENT_COMPACT}>
            {renderLeaf(activeInner.label, location.pathname)}
          </CardContent>
        </Card>
      </Box>
    )
  }

  // Branch: Item & Valuation — inner Tabs [CP List | Items and Valuation | Other List].
  const activeItems =
    ITEMS_INNER_TABS.find((t) => t.slug === innerSeg) ?? ITEMS_INNER_TABS[0]
  const itemsIndex = ITEMS_INNER_TABS.indexOf(activeItems)
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <PageHeader title={`Master Data — ${activeItems.label}`} />
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={itemsIndex < 0 ? 0 : itemsIndex}
          onChange={(_, value: number) => {
            const next = ITEMS_INNER_TABS[value]
            if (next) navigate(`/master-data/items/${next.slug}`)
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={INNER_TABS_SX}
        >
          {ITEMS_INNER_TABS.map((tab) => (
            <Tab key={tab.slug} label={tab.label} />
          ))}
        </Tabs>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          {renderLeaf(activeItems.label, location.pathname)}
        </CardContent>
      </Card>
    </Box>
  )
}
